import { INextComponent } from '@eventr/common'
import { flatten } from 'lodash'
import maplibre from 'maplibre-gl'
import { LngLatLabelledItineraryComponents } from '../interfaces/locationLabelledObject'

export interface ItineraryComponents {
    past: {
        [key: string]: INextComponent[]
    }
    present: {
        [key: string]: INextComponent[]
    }
}

/**
 * Extracts the lngLat labelled itinerary components, an array of objects with a location (i.e., longitude and latitude), as well as itinerary components filtered to include only those components at the defined location.
 * @param itineraryComponents The itinerary components.
 * @returns The lngLat labelled itinerary components.
 */
export function extractLngLatLabelledItineraryComponents(
    itineraryComponents: ItineraryComponents
): LngLatLabelledItineraryComponents[] {
    const lngLatMap = new Map<string, ItineraryComponents>()

    // Helper function to process a component and group by lngLatKey
    const processComponents = (
        components: Record<string, INextComponent[]>,
        category: 'past' | 'present'
    ) => {
        Object.entries(components).forEach(([date, componentArray]) => {
            componentArray.forEach((component) => {
                const key = lngLatKeyFromComponent(component)
                if (key) {
                    if (!lngLatMap.has(key)) {
                        lngLatMap.set(key, { past: {}, present: {} })
                    }
                    if (!lngLatMap.get(key)![category][date]) {
                        lngLatMap.get(key)![category][date] = []
                    }
                    lngLatMap.get(key)![category][date].push(component)
                }
            })
        })
    }

    // Process past and present components
    processComponents(itineraryComponents.past, 'past')
    processComponents(itineraryComponents.present, 'present')

    // Transform Map into the required result format
    return Array.from(lngLatMap.entries()).map(([key, components]) => {
        const [lon, lat] = key.split(' ')
        const comp = flatten([
            ...Object.values(components.past),
            ...Object.values(components.present),
        ])[0]

        const associatedOSMResponse =
            comp?.data?.OSMAddress ?? comp?.data?.address
        return {
            lngLat: new maplibre.LngLat(Number(lon), Number(lat)),
            associatedOSMResponse,
            object: components,
        }
    })
}

/**
 * Extracts the longitude and latitude key from a component to uniquely define a location.
 * @param component The component from which to extract the longitude and latitude key.
 * @returns The longitude and latitude key.
 */
function lngLatKeyFromComponent(component: INextComponent): string | null {
    const { lon, lat } =
        (component.data?.OSMAddress ?? component.data?.address) || {}

    return lon && lat ? `${lon} ${lat}` : null
}
